import React from 'react'

import { Form } from '@/app/component/form'
import { ActionsContainer, FormContainer } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'
import Loading from '@/app/component/guard/loading'
import DeleteProtectionToggle from '@/app/module/contacts/component/form/delete-protection-toggle'
import { useGetGroup } from '@/app/module/groups/hooks/use-get-group'
import { useUpdateGroup } from '@/app/module/groups/hooks/use-update-group'

import { fields, schema } from './specs'

type Props = {
  groupId: number

  onExit: () => void
}

const UpdateGroupForm: React.FC<Props> = ({ groupId, onExit }) => {
  const [updateGroup, { isLoading: isUpdating }] = useUpdateGroup()

  const { data, isFetching, isLoading, isUninitialized } = useGetGroup(groupId)

  const [noAutodelete, setNoAutodelete] = React.useState(data?.noAutodelete || false)
  const [changed, setChanged] = React.useState(false)
  React.useEffect(() => {
    setNoAutodelete(data?.noAutodelete || false)
  }, [data?.noAutodelete])
  const handleNoAutodeleteChange = React.useCallback((value: boolean) => {
    setNoAutodelete(value)
    setChanged(true)
  }, [])

  const handleSubmit = React.useCallback(
    async (values: { name: string }) => {
      const success = await updateGroup(groupId, { type: 'NORMAL', name: values.name, description: '', noAutodelete })
      if (!success) {
        return
      }
      onExit()
    },
    [groupId, noAutodelete, onExit, updateGroup],
  )

  const loading = isFetching || isLoading || isUninitialized || isUpdating

  const defaultValues = React.useMemo(
    () => ({
      name: data?.name || '',
    }),
    [data],
  )

  return (
    <Loading isLoading={loading} testId="group-item">
      <FormContainer>
        <Form
          defaultValues={defaultValues}
          fields={fields}
          name="update-group"
          schema={schema}
          shouldReset
          onSubmit={handleSubmit}
          renderActions={({ isDirty }) => (
            <ActionsContainer>
              <SubmitButton isDirty={changed || isDirty} isSubmitting={isUpdating}>
                Save
              </SubmitButton>
            </ActionsContainer>
          )}
        >
          <DeleteProtectionToggle checked={noAutodelete} isGroup onChange={handleNoAutodeleteChange} />
        </Form>
      </FormContainer>
    </Loading>
  )
}

export default UpdateGroupForm
